/**
 * ItemProposalFlow
 *
 * A lightweight surface for Item Proposers — users who have been invited
 * to suggest items for consideration in a prioritization cycle when the
 * item pool is still open.
 *
 * Shows:
 *   1. What this cycle is trying to decide (strategic context)
 *   2. What makes a good item suggestion (acceptance guidance)
 *   3. A simple form: name, rationale, and optional grouping
 *   4. Confirmation + "suggest another" flow
 *
 * No configuration access. No admin controls.
 * Just: understand what's needed, propose something, move on.
 *
 * Canon: §1D Goodness Principle
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Lightbulb,
  PlusCircle,
  Send,
} from 'lucide-react';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import type { DegapCycle } from '../../types/prioritization/degapCycle';

// ── Props ─────────────────────────────────────────────────────────────────────

interface ItemProposalFlowProps {
  cycle: DegapCycle;
  /** Called after a successful submission */
  onProposed?: (itemName: string) => void;
  /** Whether to show a "back to cycle" link */
  showBackLink?: boolean;
}

// ── Submission state ──────────────────────────────────────────────────────────

interface SubmittedItem {
  name: string;
  rationale: string;
}

// ── Proposal form ─────────────────────────────────────────────────────────────

function ProposalForm({
  cycle,
  onSubmit,
  isSubmitting,
}: {
  cycle: DegapCycle;
  onSubmit: (name: string, rationale: string) => Promise<void>;
  isSubmitting: boolean;
}) {
  const [name, setName] = useState('');
  const [rationale, setRationale] = useState('');

  const canSubmit = name.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    await onSubmit(name.trim(), rationale.trim());
    setName('');
    setRationale('');
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
      {/* Strategic context from cycle */}
      {cycle.description && (
        <div className="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/10 border border-[var(--slate)] dark:border-[var(--slate)]/30 px-4 py-3">
          <p className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide mb-1">
            What this cycle is deciding
          </p>
          <p className="text-sm text-[var(--text-sec)]">{cycle.description}</p>
        </div>
      )}

      {/* Item name */}
      <div>
        <label htmlFor="item-name" className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] mb-1.5">
          What are you proposing?
          <span className="ml-1 font-normal text-[var(--text-ter)]">*</span>
        </label>
        <input
          id="item-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Migrate authentication to SSO"
          className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-3 py-2.5 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          required
        />
      </div>

      {/* Rationale */}
      <div>
        <label htmlFor="item-rationale" className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] mb-1.5">
          Why does this belong in scope?
          <span className="ml-1 font-normal text-[var(--text-ter)]">(optional but recommended)</span>
        </label>
        <textarea
          id="item-rationale"
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          rows={3}
          placeholder="Briefly explain why this item should be considered — what problem it solves, what value it delivers, or what risk it addresses."
          className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-3 py-2.5 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
        />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={!canSubmit || isSubmitting}
        className="inline-flex items-center gap-2 text-sm font-medium px-5 py-2.5 bg-[var(--slate)] hover:bg-[var(--slate)] text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Send className="h-4 w-4" />
        {isSubmitting ? 'Submitting…' : 'Propose this item'}
      </button>
    </form>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function ItemProposalFlow({
  cycle,
  onProposed,
  showBackLink = true,
}: ItemProposalFlowProps) {
  const userId = useUserStore((s) => s.userId);
  const createItem = useItemsStore((s) => s.createItem);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<SubmittedItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (name: string, rationale: string) => {
    if (!userId || !cycle.id) return;
    setIsSubmitting(true);
    setError(null);
    try {
      // createItem auto-stamps createdBy (from userStore) and workspace_id (from current workspace)
      // lifecycle_state is also auto-set to 'item' by the store
      createItem({ title: name, description: rationale || undefined, tags: [] } as Parameters<typeof createItem>[0]);
      setSubmitted((prev) => [...prev, { name, rationale }]);
      onProposed?.(name);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        {showBackLink && cycle.id && (
          <Link
            to={`/prioritization/cycles/${cycle.id}`}
            className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            {cycle.name}
          </Link>
        )}
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 bg-lime-50 dark:bg-lime-900/20 flex items-center justify-center flex-shrink-0">
            <Lightbulb className="h-4 w-4 text-lime-600" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-0">
              Item pool open
            </p>
            <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Suggest items for consideration
            </h2>
          </div>
        </div>
        <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] ml-11">
          The item pool for <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">{cycle.name}</span> is accepting proposals.
          Suggest anything you believe should be considered in this prioritization.
        </p>
      </div>

      {/* Previously submitted items */}
      {submitted.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Your proposals this session
          </p>
          {submitted.map((item, i) => (
            <div
              key={i}
              className="flex items-start gap-3 bg-[var(--green-dim)] dark:bg-[var(--green)]/10 border border-[var(--green)] dark:border-[var(--green)]/30 px-4 py-3"
            >
              <CheckCircle2 className="h-4 w-4 text-[var(--green)] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
                  {item.name}
                </p>
                {item.rationale && (
                  <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5">
                    {item.rationale}
                  </p>
                )}
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-1">
            <PlusCircle className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
              Propose another below
            </p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-[var(--coral-dim)] dark:bg-[var(--coral)]/10 border border-[var(--coral)] dark:border-[var(--coral)]/30 px-4 py-3 text-sm text-[var(--coral)]">
          {error}
        </div>
      )}

      {/* Form */}
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-6 py-6">
        <ProposalForm
          cycle={cycle}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      </div>

      {/* Closing note */}
      <p className="text-xs text-center text-[var(--text-ter)] dark:text-[var(--text-ter)]">
        Proposals are reviewed by the cycle owner before items are added to the pool.
        Your name will be attached to any items you propose.
      </p>
    </div>
  );
}

export default ItemProposalFlow;
